import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Download, Mail, FileText, Users, Truck } from "lucide-react";
import { useLang } from "@/i18n/LanguageContext";
import { Reveal, SectionLabel } from "@/components/Reveal";
import { CONTACT } from "@/data/content";
import { PRIVACY_DOCS } from "@/data/privacy";

const EASE = [0.16, 1, 0.3, 1];

const DocSection = ({ s }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true }}
    transition={{ duration: 0.55, ease: EASE }}
    className="grid md:grid-cols-12 gap-4 md:gap-10 py-8 border-t border-cream/10"
  >
    <div className="md:col-span-3 flex items-start gap-3">
      <span className="font-display text-3xl font-light text-gold shrink-0 leading-none">{s.n}</span>
      <h3 className="font-display text-xl sm:text-2xl font-medium text-cream leading-snug">{s.title}</h3>
    </div>
    <div className="md:col-span-9 text-cream/75 font-light leading-relaxed text-sm sm:text-base space-y-3">
      {s.body && <p>{s.body}</p>}
      {s.intro && <p>{s.intro}</p>}
      {s.items && (
        <ul className="space-y-2">
          {s.items.map((it, i) => (
            <li key={i} className="flex gap-3">
              <span className="mt-2 w-1 h-1 rounded-full bg-gold shrink-0" />
              <span>{it}</span>
            </li>
          ))}
        </ul>
      )}
      {s.footer && <p className="pt-2">{s.footer}</p>}
    </div>
  </motion.div>
);

const Privacy = () => {
  const { lang, t } = useLang();
  const [active, setActive] = useState("clienti");
  const doc = PRIVACY_DOCS[active];

  return (
    <section
      id="privacy"
      data-testid="privacy-section"
      className="relative bg-forest-deep text-cream pt-36 sm:pt-40 pb-24 overflow-hidden min-h-[80vh]"
    >
      <div className="pointer-events-none absolute -top-24 -right-24 w-96 h-96 rounded-full bg-forest-surface/40 blur-3xl" aria-hidden />

      <div className="relative max-w-[1100px] mx-auto px-6 sm:px-10 lg:px-14">
        <SectionLabel dark>{t("privacy.label")}</SectionLabel>
        <Reveal delay={0.1}>
          <h1 className="font-display font-light tracking-tight leading-[1.0] text-4xl sm:text-5xl lg:text-6xl mt-6">
            {t("privacy.title")}
          </h1>
        </Reveal>
        <Reveal delay={0.15}>
          <p className="mt-6 max-w-3xl text-cream/75 text-base font-light leading-relaxed">
            {t("privacy.intro")}
          </p>
        </Reveal>

        {/* Tabs switcher */}
        <Reveal delay={0.2}>
          <div className="mt-10 flex flex-wrap gap-3" data-testid="privacy-tabs">
            {Object.values(PRIVACY_DOCS).map((d) => {
              const Icon = d.id === "clienti" ? Users : Truck;
              const on = active === d.id;
              return (
                <button
                  key={d.id}
                  onClick={() => setActive(d.id)}
                  data-testid={`privacy-tab-${d.id}`}
                  className={`inline-flex items-center gap-3 rounded-sm px-5 py-3 text-xs font-bold uppercase tracking-wider transition-colors duration-300 ${
                    on
                      ? "bg-gold text-forest-deep"
                      : "border border-cream/25 text-cream/75 hover:border-gold hover:text-gold"
                  }`}
                >
                  <Icon size={16} /> {d[lang].label}
                </button>
              );
            })}
          </div>
        </Reveal>

        {/* Document body */}
        <AnimatePresence mode="wait">
          <motion.article
            key={doc.id}
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -6 }}
            transition={{ duration: 0.4, ease: EASE }}
            className="mt-12"
            data-testid={`privacy-doc-${doc.id}`}
          >
            <div className="flex flex-wrap items-start justify-between gap-6 pb-6 border-b border-cream/15">
              <div className="max-w-2xl">
                <div className="flex items-center gap-3 text-gold mb-2">
                  <FileText size={18} />
                  <span className="text-[0.65rem] font-bold uppercase tracking-[0.24em]">
                    {doc[lang].short}
                  </span>
                </div>
                <h2 className="font-display text-3xl sm:text-4xl font-medium leading-tight">{doc.title}</h2>
                <p className="mt-3 text-cream/60 text-sm italic leading-relaxed">{doc.subtitle}</p>
              </div>
              <a
                href={doc.fileUrl}
                target="_blank"
                rel="noreferrer"
                download
                data-testid={`privacy-download-${doc.id}`}
                className="inline-flex items-center gap-3 rounded-sm border border-cream/25 text-cream px-5 py-3 text-xs font-bold uppercase tracking-wider hover:border-gold hover:text-gold transition-colors duration-300"
              >
                <Download size={14} /> {t("privacy.download")}
              </a>
            </div>

            <p className="mt-8 text-cream/80 font-light leading-relaxed text-base">{doc.intro}</p>

            <div className="mt-4">
              {doc.sections.map((s) => (
                <DocSection key={s.n} s={s} />
              ))}
            </div>

            <div className="mt-10 text-sm text-cream/50">
              {doc.place}, {doc.date} — I.E.P.E.M. S.r.l.
            </div>
          </motion.article>
        </AnimatePresence>

        {/* Contact */}
        <Reveal delay={0.15}>
          <div className="mt-16 border-l-2 border-gold pl-6 max-w-2xl">
            <span className="text-xs font-bold uppercase tracking-[0.24em] text-gold">
              {t("privacy.contactLabel")}
            </span>
            <p className="mt-2 text-cream/75 font-light text-sm">{t("privacy.contactIntro")}</p>
            <a
              href={`mailto:${CONTACT.email}?subject=${encodeURIComponent("Richiesta privacy / GDPR")}`}
              data-testid="privacy-email"
              className="mt-3 inline-flex items-center gap-3 text-cream hover:text-gold transition-colors"
            >
              <Mail size={16} /> {CONTACT.email}
            </a>
          </div>
        </Reveal>
      </div>
    </section>
  );
};

export default Privacy;
